import { useQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
    tasksOptions,
    annualCycleTasksOptions,
    taskOptions,
    taskOccurrencesOptions,
} from "@dashboard/api/queryApi";
import {
    createTask,
    updateTask,
    deleteTask,
    createTaskLink,
    deleteTaskLink,
    type CreateTaskInput,
    type UpdateTaskInput,
    type CreateTaskLinkInput,
} from "@dashboard/api/task";
import type { TaskQuery, TaskOccurrenceQuery } from "@dashboard/api/task";
import { useHseInstance } from "./useHseInstance";

function invalidateListQueries(queryClient: QueryClient, taskId?: number) {
    queryClient.invalidateQueries({ queryKey: ["listsWithTasks"] });
    queryClient.invalidateQueries({ queryKey: ["taskListWithTasks"] });
    queryClient.invalidateQueries({ queryKey: ["taskLists"] });
    queryClient.invalidateQueries({ queryKey: ["tasks"] });
    queryClient.invalidateQueries({ queryKey: ["taskGroups"] });
    queryClient.invalidateQueries({ queryKey: ["taskOccurrences"] });

    if (taskId) {
        queryClient.invalidateQueries(taskOptions(taskId));
    }
}

export function useTasks(query: TaskQuery = {}) {
    const { instanceId } = useHseInstance();
    return useQuery(tasksOptions({ ...query, instanceId }, true));
}

export function useAnnualCycleTasks(query: Omit<TaskQuery, "skip" | "take" | "annualCycle"> = {}) {
    const { instanceId } = useHseInstance();
    return useQuery(annualCycleTasksOptions({ ...query, instanceId }, true));
}

export function useTask(taskId: number | null) {
    return useQuery(taskOptions(taskId));
}

export function useTaskOccurrences(query: TaskOccurrenceQuery = {}) {
    const { instanceId } = useHseInstance();
    return useQuery(taskOccurrencesOptions({ ...query, instanceId }, true));
}

export function useCreateTask() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateTaskInput) => createTask(data),
        onSuccess: () => {
            invalidateListQueries(queryClient);
        },
    });
}

export function useUpdateTask() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdateTaskInput }) => updateTask(id, data),
        onSuccess: (_, variables) => {
            invalidateListQueries(queryClient, variables.id);
        },
    });
}

export function useDeleteTask() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => deleteTask(id),
        onSuccess: (_, deletedId) => {
            invalidateListQueries(queryClient, deletedId);
        },
    });
}

export function useCreateTaskLink() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ taskId, data }: { taskId: number; data: CreateTaskLinkInput }) =>
            createTaskLink(taskId, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["taskLinks", variables.taskId] });
            queryClient.invalidateQueries({ queryKey: ["tasks"] });
            queryClient.invalidateQueries(taskOptions(variables.taskId));
        },
    });
}

export function useDeleteTaskLink() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id }: { id: number; taskId: number }) => deleteTaskLink(id),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["taskLinks", variables.taskId] });
            queryClient.invalidateQueries({ queryKey: ["tasks"] });
            queryClient.invalidateQueries(taskOptions(variables.taskId));
        },
    });
}
